/**
 * Tendencia de volúmenes del pulso (007 · B1).
 * Siempre bajo el pulso. El rango lo marca el selector de arriba, no uno propio.
 */
import { useQuery } from '@tanstack/react-query'
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import { api } from '../lib/api'
import type { TendenciaPulsoApi } from '../lib/tipos'

const SERIE_1 = '#3987e5'
const SERIE_2 = '#008300'
const SERIE_3 = '#d97706'
const TINTA = '#a3a3a3'
const REJILLA = '#262626'

/** ¿Hay algún null entre dos números? (tests de honestidad) */
export function serieParteEnHuecos(valores: Array<number | null>): boolean {
    for (let i = 1; i < valores.length; i++) {
        if (valores[i] === null && valores[i - 1] !== null) return true
        if (valores[i] !== null && valores[i - 1] === null) return true
    }
    return valores.some((v) => v === null)
}

export function TendenciaPulso({
    accountId,
    dias,
    incluirTests = false,
}: {
    accountId: number
    dias: number
    incluirTests?: boolean
}) {
    const { data, isPending, isError } = useQuery<TendenciaPulsoApi>({
        queryKey: ['pulso-trends', accountId, dias, incluirTests],
        queryFn: () =>
            api.get<TendenciaPulsoApi>(
                `/business/${accountId}/pulso/trends?dias=${dias}&incluirTests=${incluirTests ? '1' : '0'}`,
            ),
        enabled: accountId > 0,
    })

    if (isPending) return <p className="text-sm text-neutral-600">Cargando estos días…</p>
    if (isError || !data) {
        return <p className="text-sm text-rose-400">No se pudo cargar la evolución.</p>
    }

    const puntos = data.dias.map((d) => ({
        etiqueta: d.fecha.slice(5),
        conversaciones: d.pulso?.conversaciones ?? null,
        citas: d.pulso?.citas ?? null,
        cancelaciones: d.pulso?.cancelaciones ?? null,
    }))
    const todosNull = puntos.every((p) => p.conversaciones === null && p.citas === null && p.cancelaciones === null)

    return (
        <section
            aria-label="Tendencia del pulso"
            className="space-y-3 rounded-xl border border-neutral-800 bg-neutral-900/30 p-5"
        >
            <h3 className="text-sm font-medium text-neutral-200">Estos días</h3>

            {todosNull ? (
                <p className="text-xs text-neutral-500">Sin datos observados en este rango.</p>
            ) : (
                <>
                    <div className="mb-2 flex flex-wrap items-center gap-5 text-sm">
                        <span className="flex items-center gap-1.5 text-neutral-400">
                            <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIE_1 }} />
                            Quien escribió
                        </span>
                        <span className="flex items-center gap-1.5 text-neutral-400">
                            <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIE_2 }} />
                            Citas hechas
                        </span>
                        <span className="flex items-center gap-1.5 text-neutral-400">
                            <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIE_3 }} />
                            Citas anuladas
                        </span>
                    </div>
                    <div className="h-[180px] w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={puntos} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                            <CartesianGrid stroke={REJILLA} vertical={false} />
                            <XAxis
                                dataKey="etiqueta"
                                tick={{ fill: TINTA, fontSize: 10 }}
                                tickLine={false}
                                axisLine={{ stroke: REJILLA }}
                            />
                            <YAxis
                                tick={{ fill: TINTA, fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                width={40}
                                allowDecimals={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: '#171717',
                                    border: '1px solid #404040',
                                    fontSize: 11,
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="conversaciones"
                                name="Conversaciones"
                                stroke={SERIE_1}
                                strokeWidth={2}
                                dot={{ r: 3, fill: SERIE_1, strokeWidth: 0 }}
                                connectNulls={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="citas"
                                name="Citas"
                                stroke={SERIE_2}
                                strokeWidth={2}
                                dot={{ r: 3, fill: SERIE_2, strokeWidth: 0 }}
                                connectNulls={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="cancelaciones"
                                name="Cancelaciones"
                                stroke={SERIE_3}
                                strokeWidth={2}
                                dot={{ r: 3, fill: SERIE_3, strokeWidth: 0 }}
                                connectNulls={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    </div>
                </>
            )}
        </section>
    )
}
